import { useState } from 'react';
import { CheckOutlined, FileAddOutlined, FileOutlined } from '@ant-design/icons';

import { Button, Checkbox, Dialog, Field, Input, Select, Textarea } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { LOAI_VAN_BAN } from '../../nghiep-vu/loai-van-ban';
import { laTepChu, tenTuTep, tepGuiLen } from '../../nghiep-vu/tom-tat';
import type { ThemTomTat } from '../../types-tom-tat';

interface Form {
  tieuDe: string;
  loai: string;
  noiDung: string;
  tuDong: boolean;
  tep: File | null;
}

/** Mặc định loại đầu danh sách ("Công văn"), như bản cũ. */
const RONG: Form = { tieuDe: '', loai: LOAI_VAN_BAN[0] ?? '', noiDung: '', tuDong: true, tep: null };

/**
 * Hộp "Thêm văn bản mới" của bản cũ: tên, loại, tải tệp (PDF, DOCX, TXT, MD — backend tự trích chữ) hoặc dán
 * nội dung, tự tóm tắt ngay sau khi thêm. Người tạo do backend lấy từ người đang đăng nhập.
 */
export function ThemVanBanDialog({
  open,
  saving,
  onClose,
  onSubmit,
}: {
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: (vao: ThemTomTat) => void;
}) {
  const { t } = useT();
  const [f, setF] = useState<Form>(RONG);
  const [loi, setLoi] = useState<{ tieuDe?: string; noiDung?: string }>({});

  const chonTep = (tep: File) => {
    setF((cu) => ({ ...cu, tep, tieuDe: cu.tieuDe || tenTuTep(tep.name) }));
    // `.txt` đọc luôn để người dùng xem, sửa trước khi tóm tắt
    if (laTepChu(tep.name)) {
      void tep.text().then((noiDung) => setF((cu) => ({ ...cu, noiDung })));
    }
  };

  const gui = () => {
    const tieuDe = f.tieuDe.trim();
    const tep = tepGuiLen(tieuDe, f.noiDung, f.tep);
    const moi = {
      tieuDe: tieuDe ? undefined : t('vanBanTomTat.loiThieuTen'),
      noiDung: tep ? undefined : t('vanBanTomTat.loiThieuNoiDung'),
    };
    setLoi(moi);
    if (moi.tieuDe || moi.noiDung || !tep) return;
    onSubmit({ document_name: tieuDe, document_type: f.loai, auto_summarize: f.tuDong, file: tep });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={520}
      destroyOnHidden
      afterOpenChange={(mo) => {
        if (mo) {
          setF(RONG);
          setLoi({});
        }
      }}
      title={
        <span className="flex items-center gap-2">
          <FileAddOutlined className="text-primary-500" />
          {t('vanBanTomTat.themTieuDe')}
        </span>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button variant="primary" icon={<CheckOutlined />} loading={saving} onClick={gui}>
            {saving ? t('chung.dangLuu') : t('vanBanTomTat.them')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t('vanBanTomTat.oTen')} required error={loi.tieuDe}>
          {(p) => (
            <Input
              {...p}
              value={f.tieuDe}
              placeholder={t('vanBanTomTat.oTenViDu')}
              onChange={(e) => setF((cu) => ({ ...cu, tieuDe: e.target.value }))}
            />
          )}
        </Field>
        <Field label={t('vanBanTomTat.oLoai')}>
          {(p) => (
            <Select
              {...p}
              className="w-full"
              value={f.loai}
              options={LOAI_VAN_BAN.map((x) => ({ value: x, label: x }))}
              onChange={(v) => setF((cu) => ({ ...cu, loai: String(v) }))}
            />
          )}
        </Field>
        <Field label={t('vanBanTomTat.oTep')}>
          {(p) => (
            <div>
              <input
                {...p}
                type="file"
                accept=".txt,.md,.docx,.pdf"
                onChange={(e) => {
                  const tep = e.target.files?.[0];
                  if (tep) chonTep(tep);
                }}
                className="w-full rounded border border-default bg-surface px-3 py-1.5 text-sm text-content-secondary file:mr-3 file:rounded-sm file:border-0 file:bg-subtle file:px-2 file:py-1 file:text-xs"
              />
              {f.tep && (
                <div className="mt-2 inline-flex max-w-full items-center gap-2 rounded border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs text-primary-700">
                  <FileOutlined className="shrink-0" /> <span className="truncate">{f.tep.name}</span>
                </div>
              )}
            </div>
          )}
        </Field>
        <Field label={t('vanBanTomTat.oNoiDung')} error={loi.noiDung}>
          {(p) => (
            <Textarea
              {...p}
              rows={8}
              value={f.noiDung}
              placeholder={t('vanBanTomTat.oNoiDungViDu')}
              onChange={(e) => setF((cu) => ({ ...cu, noiDung: e.target.value }))}
            />
          )}
        </Field>
        <Checkbox checked={f.tuDong} onChange={(e) => setF((cu) => ({ ...cu, tuDong: e.target.checked }))}>
          {t('vanBanTomTat.tuDongTomTat')}
        </Checkbox>
      </div>
    </Dialog>
  );
}
